import { App } from '@capacitor/app';
import { isNative } from './platform';

type BackHandler = () => void;

/**
 * Handlers that claim the hardware back button ahead of the router, most recent first. An overlay
 * that has its own "back" — the chat dock stepping from a thread to its list — claims it while that
 * step exists, so back undoes the last thing the user opened instead of leaving the page under it.
 */
const handlers: BackHandler[] = [];

/**
 * Claim the next hardware back press. Returns the release; call it when the step is gone. Handlers
 * stack, so the innermost overlay answers first and releasing out of order is safe. Off native the
 * claim is only held: no listener reads the stack there (`setupBackButton`).
 */
export const pushBackHandler = (handler: BackHandler): (() => void) => {
    handlers.push(handler);
    return () => {
        const index = handlers.lastIndexOf(handler);
        if (index !== -1) {
            handlers.splice(index, 1);
        }
    };
};

export const setupBackButton = (history: { goBack: () => void }) => {
    if (!isNative()) return;

    const listener = App.addListener('backButton', ({ canGoBack }) => {
        const claimed = handlers[handlers.length - 1];
        if (claimed !== undefined) {
            claimed();
        } else if (canGoBack) {
            history.goBack();
        } else {
            App.exitApp();
        }
    });

    return () => {
        listener.then((h) => h.remove());
    };
};
